import { type Product, type Link } from 'types/adtech';
import { progressiveGridSizing } from 'utils/gridUtils';
import { TRADING_CELL } from '@johnlewispartnership/wtr-content-component-library/dist/component-library/constants/cmsResourceTypes';
import { generateBannerLink } from './generate-banner-link';

interface DeviceBreakpoints {
  width: string;
  name: string;
  auto: boolean;
  layout: string;
  visible: boolean;
}

export interface CitrusProduct extends Product {
  id: string;
  name: string;
}

interface Banner {
  id: string;
  name: string;
  type: string;
  gridPosition: string;
  adTechPosition: string;
  image: { src: string; altText: string; mode: string };
  textArea: {
    heading: string;
    color: string;
    subheading: string;
    offerHeading: string;
    link: Link;
  };
  products: CitrusProduct[];
  marketingBadge?: 'none' | 'New' | 'Try';
  cqResponsive: {
    deviceBreakpoints: DeviceBreakpoints[];
  };
  tracking?: {
    vendor: string;
  };
}

interface ComponentProps {
  banner: Banner;
}

export interface TradingCellPropsFactoryProps {
  component: ComponentProps;
  position?: string;
  index?: number;
  searchType: string;
}

export const transformSearchResultToCitrusTradingCell = ({
  component,
  position = '',
  index,
  searchType,
}: TradingCellPropsFactoryProps) => {
  const {
    banner: {
      textArea,
      id,
      name,
      image: { src, altText },
      marketingBadge,
      products,
      cqResponsive,
    },
  } = component;

  const formattedProducts = products?.map(product => ({
    id: product.id,
  }));

  const breakpointClasses = progressiveGridSizing([], cqResponsive, 12, true);

  let { link } = textArea;
  link = generateBannerLink(link?.url, products);

  const analyticsTags = {
    'data-analytics-id': id,
    'data-analytics-name': name,
    'data-analytics-position': position,
    'data-analytics-sponsored': `CitrusAd-SponsoredBanner-${searchType}-${
      component.banner.type
    }-${textArea.offerHeading.replace(/ /g, '')}-${marketingBadge ?? ''}-${name}`,
  };

  return {
    ...component,
    externalImageSrc: true,
    products: formattedProducts,
    index,
    isCitrusAd: true,
    textArea: {
      ...textArea,
      link,
    },
    position,
    componentId: id,
    cqResponsive,
    breakpointClasses,
    defaultNumOfCells: 'twoCells',
    resourceType: TRADING_CELL,
    icons: {
      messageIcon: {
        display: !!marketingBadge,
        data: {
          message: marketingBadge,
        },
      },
      offerRoundel: {
        display: !!textArea?.offerHeading,
        image: {
          altText: 'Offers',
          src: '/content/dam/waitrosegroceriescms/icons/offerroundels/offers.png',
        },
      },
    },
    image: {
      landscapeImage: { src, altText, assetMetadataHeight: '986', assetMetadataWidth: '1500' },
    },
    analyticsTags,
    analytics: { promoName: 'citrusAd' as const, ...component.banner.tracking },
  };
};
